/**
 * Interactive scripture block — each word is clickable when it has
 * Strong's numbers. Clicking a word toggles the WordAccordion below.
 */

import { useState, useCallback } from 'react';
import type { ScriptureVerse, ScriptureWord } from '@skjv/common';
import { WordAccordion } from './word-accordion';

interface InteractiveScriptureBlockProps {
  verse: ScriptureVerse;
}

export function InteractiveScriptureBlock({ verse }: InteractiveScriptureBlockProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleWordClick = useCallback((index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  }, []);

  const expandedWord = expandedIndex !== null ? verse.words[expandedIndex] : null;

  return (
    <blockquote className="interactive-scripture">
      <p className="scripture-block-label">It is written...</p>
      <p className="interactive-scripture-text">
        &ldquo;{verse.words.map((w, i) => (
          <WordSpan
            key={i}
            word={w}
            index={i}
            isExpanded={expandedIndex === i}
            onClick={handleWordClick}
          />
        ))}&rdquo;
      </p>
      <cite className="scripture-block-ref">&mdash; {verse.reference}</cite>

      {expandedWord && expandedWord.strongsNumbers.length > 0 && (
        <WordAccordion word={expandedWord} />
      )}
    </blockquote>
  );
}

interface WordSpanProps {
  word: ScriptureWord;
  index: number;
  isExpanded: boolean;
  onClick: (index: number) => void;
}

function WordSpan({ word, index, isExpanded, onClick }: WordSpanProps) {
  const hasStrongs = word.strongsNumbers.length > 0;
  const className = [
    hasStrongs ? 'clickable-word' : undefined,
    isExpanded ? 'clickable-word-active' : undefined,
    word.isJesusWord ? 'jesus-word' : undefined,
  ].filter(Boolean).join(' ') || undefined;

  const pre = word.precedingPunctuation;

  if (!hasStrongs) {
    return <span className={className}>{pre}{word.word}{word.succeedingPunctuation}</span>;
  }

  return (
    <>
      {pre && <span>{pre}</span>}
      <button
        type="button"
        className={className}
        onClick={() => onClick(index)}
      >
        {word.word}
      </button>
      {word.succeedingPunctuation && <span>{word.succeedingPunctuation}</span>}
    </>
  );
}
